import { cn } from '@liza/ui/utils';
import { ChevronDownIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';

export type FamilyHistoryGroupRowProps = {
  title: string;
  description?: string;
  /** Shown in the muted badge ahead of the title, e.g. how many resources collapsed into this row. */
  count?: number;
  ariaLabel: string;
  testId: string;
  /** Ids of the resources this row stands for; the row opens when one of them is selected. */
  recordIds: string[];
  selectedId?: string;
  children: React.ReactNode;
};

/**
 * A collapsed header that expands to the rows it summarises: a relative built from several
 * resources, a condition and the relatives who have it, the pertinent negatives. Opens on first
 * render when it holds the selected record and re-opens when a later selection lands inside it.
 */
export const FamilyHistoryGroupRow = ({
  title,
  description,
  count,
  ariaLabel,
  testId,
  recordIds,
  selectedId,
  children,
}: FamilyHistoryGroupRowProps): React.ReactElement => {
  const containsSelected = selectedId !== undefined && recordIds.includes(selectedId);
  const [open, setOpen] = useState(containsSelected);

  useEffect(() => {
    if (containsSelected) setOpen(true);
  }, [containsSelected, selectedId]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} data-testid={testId}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-auto w-full items-center justify-between gap-2 px-3 py-2 text-left font-normal"
          aria-label={ariaLabel}
          data-testid="family-history-group-trigger"
        >
          <span className="flex min-w-0 flex-col">
            <span className="flex items-center gap-1.5 text-sm font-medium">
              {count !== undefined && count > 1 && (
                <span
                  className="shrink-0 rounded-sm bg-muted px-1 py-0.5 text-xs font-medium text-muted-foreground tabular-nums"
                  data-testid="record-list-item-count"
                >
                  {count}×
                </span>
              )}
              <span className="truncate">{title}</span>
            </span>
            {description && <span className="text-xs text-muted-foreground">{description}</span>}
          </span>
          <ChevronDownIcon
            className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-4 space-y-1 border-l pl-2">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
};
